<template>
  <div
    :class="DS_ICON_PICKER_LIST_CLASSES.Grid"
    data-cy="ds-icon-picker-list__grid"
    :aria-keyshortcuts="props.hasMore ? ICON_PICKER_LOAD_MORE_KEY : undefined"
    @scroll="onScroll"
    @keydown="onKeydown"
  >
    <DSButton
      v-for="icon in props.icons"
      :key="icon"
      :variant="DS_BUTTON_VARIANT.Ghost"
      :size="DS_SIZE.Medium"
      :disabled="props.disabled"
      :aria-selected="icon === modelValue"
      :aria-keyshortcuts="props.hasMore ? ICON_PICKER_LOAD_MORE_KEY : undefined"
      :data-cy="`ds-icon-picker-list__item--${icon}`"
      :prefix-icon="icon"
      @click="onSelect(icon)"
    />
  </div>
</template>

<script setup lang="ts">
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { DS_ICON_PICKER_LIST_CLASSES } from "@/src/design_system/form_elements/icon_picker/constants/classes";
import {
  ICON_PICKER_LOAD_MORE_KEY,
  ICON_PICKER_LOAD_MORE_SCROLL_OFFSET_PX,
} from "@/src/design_system/form_elements/icon_picker/constants/pagination";
import type { DSIconPickerGridProps } from "@/src/design_system/form_elements/icon_picker/types/props";
import type { DSIconPickerGridEmits } from "@/src/design_system/form_elements/icon_picker/types/emits";

const props = withDefaults(defineProps<DSIconPickerGridProps>(), {
  disabled: false,
  hasMore: false,
});

const emit = defineEmits<DSIconPickerGridEmits>();

const modelValue = defineModel<DSIconName | null>({ default: null });

const onSelect = (icon: DSIconName): void => {
  modelValue.value = icon;
};

const onScroll = (event: Event): void => {
  if (!props.hasMore) return;

  const grid = event.currentTarget as HTMLElement;
  const remainingScroll = grid.scrollHeight - grid.scrollTop - grid.clientHeight;

  if (remainingScroll <= ICON_PICKER_LOAD_MORE_SCROLL_OFFSET_PX) {
    emit("load-more");
  }
};

const onKeydown = (event: KeyboardEvent): void => {
  if (!props.hasMore || event.key !== ICON_PICKER_LOAD_MORE_KEY) return;

  event.preventDefault();
  emit("load-more");
};
</script>
